import React from "react";
import { 
  ShieldCheck, 
  Award, 
  Users, 
  Clock, 
  CheckCircle, 
  Sparkles, 
  ThumbsUp,
  Star,
  Home,
  Building,
  Truck,
  Leaf,
  Target,
  Heart
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const AboutUs = () => {
  const teamMembers = [
    {
      name: "Sam Knight",
      role: "Founder & CEO",
      experience: "18 years",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80",
      quote: "Cleanliness is not just a service, it's a commitment to health and wellbeing."
    },
    {
      name: "Sarah Johnson",
      role: "Operations Director",
      experience: "12 years",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80",
      quote: "We treat every home as if it were our own."
    },
    {
      name: "Nick Chen",
      role: "Technical Specialist",
      experience: "15 years",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80",
      quote: "Innovation in cleaning technology drives our exceptional results."
    },
    {
      name: "Emma Rodriguez",
      role: "Customer Relations",
      experience: "8 years",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80",
      quote: "Our clients' satisfaction is our top priority."
    }
  ];

  const stats = [
    { icon: <Home size={24} />, value: "15,000+", label: "Homes Cleaned" },
    { icon: <Building size={24} />, value: "2,500+", label: "Business Clients" },
    { icon: <Users size={24} />, value: "98.7%", label: "Customer Satisfaction" },
    { icon: <Award size={24} />, value: "250+", label: "5-Star Reviews" },
    { icon: <Clock size={24} />, value: "24/7", label: "Emergency Service" },
    { icon: <Truck size={24} />, value: "45 min", label: "Average Response Time" }
  ];

  const values = [
    {
      icon: <ShieldCheck className="text-blue-600" size={28} />,
      title: "Integrity First",
      description: "We believe in honest pricing and transparent communication with every client."
    },
    {
      icon: <Sparkles className="text-indigo-600" size={28} />,
      title: "Excellence in Service",
      description: "Every job receives the same high standard of attention to detail and quality."
    },
    {
      icon: <Leaf className="text-green-600" size={28} />,
      title: "Eco-Friendly Approach",
      description: "Using environmentally safe products that are tough on dirt, gentle on the planet."
    },
    {
      icon: <Users className="text-amber-600" size={28} />,
      title: "Team Excellence",
      description: "Our trained professionals are background-checked and continuously educated."
    }
  ];

  const certifications = [
    { name: "IICRC Certified", icon: "🏅" },
    { name: "Green Seal Certified", icon: "🌿" },
    { name: "OSHA Compliant", icon: "✅" },
    { name: "Insured & Bonded", icon: "🛡️" },
    { name: "COVID-19 Safety", icon: "🦠" },
    { name: "Australian Standards", icon: "🇦🇺" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Helmet>
      <title>
        About Knight Cleanings | Melbourne Cleaning Experts
      </title>

      <meta
        name="description"
        content="Knight Cleanings is a Melbourne-based cleaning company delivering reliable, high-quality services for homes and businesses."
      />

      <meta
        name="keywords"
        content="
        Carpet cleaning Melbourne,
        Upholstery cleaning Melbourne,
        End of lease cleaning Melbourne,
        Mattress cleaning Melbourne,
        Professional cleaning Melbourne,

        Carpet cleaning Dandenong,
        Upholstery cleaning Dandenong,
        End of lease cleaning Dandenong,

        Carpet cleaning Hallam,
        Upholstery cleaning Hallam,

        Carpet cleaning Hampton Park,
        Carpet cleaning Clayton,
        Carpet cleaning Keysborough,
        Carpet cleaning Glen Waverley,
        Carpet cleaning Mount Waverley,
        Carpet cleaning Noble Park,

        Carpet cleaning Lynbrook,
        Carpet cleaning Doveton,

        Mattress cleaning Mornington,
        Carpet cleaning Mornington,
        Carpet cleaning Rye,
        Carpet cleaning Rosebud,

        Professional cleaners Melbourne,
        Steam carpet cleaning Melbourne,
        Deep cleaning services Melbourne,
        Residential cleaning Melbourne,
        Commercial cleaning Melbourne
        "
      />

      <meta name="robots" content="index, follow" />
      <meta name="author" content="Professional Cleaning Services Melbourne" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Knight Cleanings",
            "url": "https://knightcleanings.com.au/about",
            "mainEntity": {
              "@type": "CleaningService",
              "name": "Knight Cleaning",
              "url": "https://knightcleanings.com.au/",
              "description": "Melbourne-based cleaning company delivering reliable, high-quality services for homes and businesses.",
              "telephone": "+61420499661",
              "email": "info@knightcleanings.com.au",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "11 Warath Drive, Dandenong North",
                "addressLocality": "Melbourne",
                "addressRegion": "VIC",
                "postalCode": "3175",
                "addressCountry": "AU"
              },
              "areaServed": [
                { "@type": "City", "name": "Melbourne" },
                { "@type": "City", "name": "Dandenong" },
                { "@type": "City", "name": "Mornington Peninsula" }
              ],
              "founder": {
                "@type": "Person",
                "name": "Founder Name" // Add your founder's name if known
              },
              "foundingDate": "2020", // Add your founding year
              "numberOfEmployees": {
                "@type": "QuantitativeValue",
                "value": "10-20" // Adjust as needed
              }
            }
          })}
        </script>
    </Helmet>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6 leading-tight">
                Transforming Spaces, <span className="text-amber-300">Enhancing Lives in Melbourne VIC</span>
              </h1>
              <h2 className="text-3xl font-semibold mb-4">
                Melbourne's Premier Cleaning Service Provider
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                For over 15 years, Knight Cleanings has been Melbourne's trusted partner for 
                premium cleaning solutions. We combine cutting-edge technology with 
                old-fashioned attention to detail.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg"
                >
                  Get Your Free Quote
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-full transition-all duration-300"
                >
                  Our Services
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80" 
                alt="Professional Cleaning Team"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-2xl">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-3 rounded-full">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">15+ Years</p>
                    <p className="text-gray-600">Of Excellence</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-lg">OUR JOURNEY</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">The Knight Cleanings Story</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=500&q=80" 
                  alt="Our Beginning"
                  className="rounded-2xl shadow-xl w-full h-auto"
                />
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-2xl shadow-xl">
                  <p className="text-2xl font-bold">Est. 2008</p>
                  <p className="text-blue-100">Serving Melbourne</p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                From Humble Beginnings to Industry Leaders
              </h3>
              <p className="text-gray-600 mb-6 text-lg">
                Founded in 2008 by Michael Knight, our company started with a single van 
                and a vision: to provide cleaning services that exceed expectations. 
                What began as a small family business has grown into Melbourne's premier 
                cleaning service provider.
              </p>
              <p className="text-gray-600 mb-8 text-lg">
                Today, we serve thousands of satisfied residential and commercial clients, 
                but we've never lost sight of our core values: integrity, excellence, 
                and genuine care for our community.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-500">
                  <div className="flex items-center gap-3 mb-3">
                    <Target className="text-blue-600" size={24} />
                    <div className="text-blue-600 font-bold text-2xl">Our Mission</div>
                  </div>
                  <p className="text-gray-700">
                    To provide exceptional cleaning services that enhance living and working environments through innovative solutions and unparalleled customer care.
                  </p>
                </div>
                <div className="bg-indigo-50 p-6 rounded-xl border-l-4 border-indigo-500">
                  <div className="flex items-center gap-3 mb-3">
                    <Heart className="text-indigo-600" size={24} />
                    <div className="text-indigo-600 font-bold text-2xl">Our Vision</div>
                  </div>
                  <p className="text-gray-700">
                    To become Melbourne's most trusted and sustainable cleaning service, setting new standards for quality and environmental responsibility.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-blue-50 to-indigo-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-white p-4 rounded-2xl shadow-lg inline-flex items-center justify-center mb-4">
                  <div className="text-blue-600">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-lg">OUR CORE VALUES</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">What Makes Us Different</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence goes beyond just cleaning – it's about creating 
              lasting relationships and environments where people thrive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-gray-100"
              >
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl inline-flex mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-lg">MEET OUR EXPERTS</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our team of dedicated professionals brings decades of combined experience 
              to ensure every cleaning project exceeds expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="text-white font-bold text-lg">{member.name}</div>
                    <div className="text-blue-300 text-sm">{member.role}</div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="text-amber-400" size={16} />
                    <span className="text-sm text-gray-600">{member.experience} experience</span>
                  </div>
                  <p className="text-gray-600 italic">"{member.quote}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-lg">CERTIFICATIONS & STANDARDS</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">Industry Recognized Excellence</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
            {certifications.map((cert, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 text-center group hover:-translate-y-1"
              >
                <div className="text-3xl mb-4">{cert.icon}</div>
                <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{cert.name}</div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-10 text-white relative overflow-hidden">
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-10"
              style={{
                backgroundImage: 'url("https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")'
              }}
            ></div>
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Ready to Experience the Knight Cleanings Difference?</h3>
                <p className="text-blue-100 text-lg mb-6">
                  Join thousands of satisfied customers who trust us with their cleaning needs. 
                  Book your service today and see why we're Melbourne's preferred cleaning service.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-blue-700 hover:bg-gray-100 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-lg text-center"
                >
                  Schedule Service
                </Link>
                <a
                  href="tel:+61420499661"
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-full transition-all duration-300 text-center"
                >
                  Call Now: +61 420 499 661
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;